import { useNavigate } from 'react-router-dom';
import { countLimit } from '../../../../utils/globalUtils';
import { FEATURE_KEYS, getFeatureApis } from '../../../../config/featureRegistry';
import useToast from '../../../../hooks/useToast';
import analytics from '../../../../utils/analytics';
import { dashboardCardConstants, ROUTES_PATH } from '../../../../constants/globalConstant';
import { useWebTeamsData } from '../../web/WebTeamsDataContext';

/**
 * Card-action handlers for the WebTeams screen's dashboard cards.
 *
 * Mirrors the launchbar/object360 pattern: looks up the CasaWebHomepage
 * feature config once and calls its API surface directly (same endpoints
 * the feature-route listing uses via useFeature()). WebTeams is not a
 * feature route so useFeature() can't be used here — getFeatureApis is
 * the equivalent escape hatch.
 *
 * Listing data (dashboardsById) and V2-team refetch come from
 * WebTeamsDataContext, which the WebTeams screen populates.
 */
function useWebHomepageCard() {
  const { dashboardsById, refetchDashboards, refetchTeamsV2 } = useWebTeamsData();
  const navigate = useNavigate();
  const toast = useToast();

  const webFeatureApis = getFeatureApis(FEATURE_KEYS.WEB_HOMEPAGE)?.apis;

  const sendEvent = (name, category, homepage) => {
    analytics.sendEvent(name, {
      category,
      type: 'click',
      homepageId: homepage?.id,
      status: homepage?.status,
      homepageName: homepage?.name,
      tab: 'Homepage View'
    });
  };

  const cloneWebHomepageClicked = async (homepage, sourceClicked = '') => {
    if (Object.keys(dashboardsById).length >= countLimit.WEB_HOMEPAGES_COUNT) {
      toast.warn(`Maximum ${countLimit.WEB_HOMEPAGES_COUNT} homepages can be added`);
      return;
    }
    const homepageId = homepage?.id;
    const response = await webFeatureApis.clone(homepageId);
    if (response?.status === 200) {
      navigate(`${ROUTES_PATH.webHomepageBuilder}/${homepageId}`);
    }
    analytics.sendEvent(`clone web homepage ${sourceClicked && ' clicked from ' + sourceClicked}`, {
      category: 'clone_web_homepage',
      type: 'click'
    });
  };

  const createHomepageClicked = (sourceClicked = '') => {
    if (Object.keys(dashboardsById).length < countLimit.HOMEPAGES_COUNT) {
      // Navigate to builder without ID - BuilderDataContext will auto-create
      navigate(ROUTES_PATH.homebuilder);

      analytics.sendEvent(
        `create new homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
        { category: 'create_new_homepage', type: 'click' }
      );
    } else {
      toast.warn(`Maximum ${countLimit.HOMEPAGES_COUNT} homepages can be added`);
    }
  };

  const deleteHomepageClicked = async (homepage, sourceClicked = '') => {
    const response = await webFeatureApis.delete(homepage.id);
    if (response?.status === 200) {
      toast.success(`${homepage.name} was deleted successfully!`);
      refetchDashboards();
      refetchTeamsV2();
    }
    sendEvent(
      `delete homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'delete_homepage',
      homepage
    );
  };

  const unpublishHomepageClicked = async (homepage, sourceClicked = '') => {
    const response = await webFeatureApis.unpublish(homepage.id);
    if (response?.status === 200) {
      toast.success(`${homepage.name} was successfully ${dashboardCardConstants.UNPUBLISHED}!`);
      refetchDashboards();
      refetchTeamsV2();
    }
    sendEvent(
      `unpublish homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'unpublish_homepage',
      homepage
    );
  };

  const publishHomepageClicked = async (homepage, sourceClicked = '') => {
    const response = await webFeatureApis.publish(homepage.id, { addTeams: [] });
    if (response?.status === 200) {
      toast.success(`${homepage.name} was successfully published!`);
      refetchDashboards();
      refetchTeamsV2();
    }
    sendEvent(
      `publish homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'publish_homepage',
      homepage
    );
  };

  const unassignHompeageClicked = async (homepage, teamId, sourceClicked = '') => {
    const postBody = {
      addTeams: [],
      deleteTeams: [teamId],
      isDefault: false
    };
    const response = await webFeatureApis.updateTeams(homepage.id, postBody);
    if (response?.status === 200) {
      toast.success(`${homepage.name} was unassigned from the team!`);
      refetchDashboards();
      refetchTeamsV2();
    }
    sendEvent(
      `unassign homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'unassign_homepage',
      homepage
    );
  };

  return {
    cloneWebHomepageClicked,
    createHomepageClicked,
    deleteHomepageClicked,
    unpublishHomepageClicked,
    publishHomepageClicked,
    unassignHompeageClicked
  };
}

export default useWebHomepageCard;
